import { axisBottom } from 'd3-axis';
import { select } from 'd3-selection';
import { scaleBand, scaleLinear } from 'd3-scale';
import { Util } from 'core/services';

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
export class BarChart {
    private el: any;
    private yAxisSymbol: string;
    private svg: any;
    private barWidth: number = 12;
    private _class: string = 'ma-bar';
    private selectedClass: string = 'ma-bar-selected';
    private width: number = 0;
    private height: number = 0;
    private data: GraphItem[];
    private scaleX: any;
    private scaleY: any;
    private bars: any;

    constructor(el: any, data: GraphItem[]) {
        this.el = el;
        this.svg = select(this.el);
        this.data = data;
    }

    public setYAxisSymbol(symbol: string): void {
        this.yAxisSymbol = symbol;
    }

    public update(): void {
        this.width = this.el.getBoundingClientRect().width ;
        this.height = this.el.getBoundingClientRect().height;

        // Make sure starting with clean svg element
        this.svg.selectAll('*').remove();

        const hLineStartX: number = 22;
        this.scaleX = scaleBand().range([ 0, this.width ]);
        this.scaleY = scaleLinear().range([ 0, this.height ]);

        const max: number = this.data.reduce((maxItem: number, item: GraphItem) => (item.value > maxItem) ? item.value : maxItem, 0);
        const min: number = this.data.reduce((minItem: number, item: GraphItem) => (item.value < minItem) ? item.value : minItem, 1000);

        const useZeroScale: boolean = max <= 2; // 2 GB or $2 min;

        // Round up to nearest even number
        let maxVal: number = Math.ceil(max / 2) * 2;
        let midVal: number = maxVal * 0.5;
        let minVal: number = Math.round(min);

        if (minVal < 0) {
            minVal = 0;
        }

        if (useZeroScale) {
            // Handle 0 value
            midVal = 1;
            maxVal = 2;
        }

        this.scaleX.domain(this.data.map((d: GraphItem) => d.date));

        this.scaleY.domain([ 0, maxVal ]);

        const y: any = this.scaleY;
        const h: number = this.height;
        const halfWidth: number = this.barWidth * 0.5;

        // HORIZONTAL LINES & LABELS
        // These are little too custom to just use a y-axis
        const hLineOffset: number = 5;
        const midYpoint: number = (this.height - hLineOffset) * 0.5;

        // TOP H-LINE
        this.svg.append('line')
            .attr('class', 'h-line')
            .style('fill', 'none')
            .attr('x1', hLineStartX)
            .attr('x2', this.width)
            .attr('y1', hLineOffset)
            .attr('y2', hLineOffset);
        const topVal: number = useZeroScale ? 2 : this.getYaxisVal(maxVal);
        const topText: any = this.svg.append('text')
            .attr('class', 'y-label')
            .attr('y', 10)
            .text(topVal);

        // MID H-LINE
        this.svg.append('line')
            .attr('class', 'h-line')
            .style('fill', 'none')
            .style('stroke-width', 2)
            .attr('x1', hLineStartX)
            .attr('x2', this.width)
            .attr('y1', midYpoint)
            .attr('y2', midYpoint);

        const midText: any = this.svg.append('text')
            .attr('class', 'y-label')
            .attr('y', (midYpoint + 3));

        if (topVal > 1) {
            midText.text(useZeroScale ? 1 : this.getYaxisVal(midVal));
        }

        this.svg.append('line')
            .attr('class', 'h-line')
            .style('fill', 'none')
            .style('stroke-width', 2)
            .attr('x1', hLineStartX)
            .attr('x2', this.width)
            .attr('y1', this.height)
            .attr('y2', this.height);

        const bottomText: any = this.svg.append('text')
            .attr('class', 'y-label')
            .attr('y', this.height + 3)
            .text(this.yAxisSymbol);

        // Resolve right-align issues
        const textPadding: number = 3;
        topText.attr('x', (hLineStartX - ((<number> topText.node().getBBox().width) + textPadding)));
        midText.attr('x', (hLineStartX - ((<number> midText.node().getBBox().width) + textPadding)));
        bottomText.attr('x', (hLineStartX - ((<number> bottomText.node().getBBox().width) + textPadding)));

        const barHolder: any = this.svg.append('g')
            .attr('class', 'bar-holder')
            .attr('transform', 'translate(0, 0)');

        this.bars = barHolder.selectAll('.bar')
            .data(this.data)
            .enter()
            .append('rect')
            .attr('class', (d: GraphItem) => d.selected ? this.selectedClass : this._class);

        const barHeightOffset: number = 5;

        /* tslint:disable:no-unused-variable */
        this.bars
            .attr('x', (_d: GraphItem, i: number) => ((this.width / this.data.length) * i) + (this.width / (this.data.length * 2)) - halfWidth)
            .attr('width', this.barWidth)
            .attr('y', (d: GraphItem) => ((h - (this.minheight(d.value, y))) + barHeightOffset))
            .attr('height', (d: GraphItem) => ((this.minheight(d.value, y) || barHeightOffset) - barHeightOffset))
            .attr('ry', halfWidth)
            .attr('rx', halfWidth);

        /* tslint:enable:no-unused-variable */

        // X AXIS
        const _xAxis: any = axisBottom(this.scaleX).tickFormat((d: Date) => Util.dateFormat(d.toString(), 'MMM'));

        barHolder.append('g')
            .attr('id', 'xaxis')
            .attr('class', 'x axis custom-x-axis')
            .attr('transform', `translate(0 ,${this.height})`)
            .call(_xAxis);
    }

    private minheight(val: number, f: any): number {
        if (val <= 0) {
            return 0;
        }

        let n: number = f(val);
        if (n < 15) {
            n = 15;
        }

        return n;
    }

    private getYaxisVal(val: number): number {
        const num: number = Math.ceil(val);

        return num < 1 ? 0 : num;
    }

}
/* eslint-enable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/explicit-module-boundary-types */
